import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	Input,
	NgZone,
	OnChanges,
	OnDestroy,
	SimpleChange,
	ViewEncapsulation,
} from "@angular/core";
import {
	fromMillisecondsToHours,
	fromMillisecondsToMinutes,
	fromMillisecondsToSeconds,
	PlatformService,
} from "@obg/ngx.global";
import { differenceInMilliseconds } from "date-fns";
import { floor, padStart } from "lodash-es";
import { Subscription, timer } from "rxjs";
import { tap } from "rxjs/operators";

const REFRESH_TIMER_MILLISECONDS = 1000;

@Component({
	selector: "obg-timer",
	templateUrl: "./timer.component.html",
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class TimerComponent implements OnChanges, OnDestroy {
	@HostBinding("attr.test-id") testId = "obg-timer";

	@Input() elapsedTimeInMillis: number | undefined;
	@Input() formattableText: string | undefined;

	hours: string | undefined;
	minutes: string | undefined;
	seconds: string | undefined;

	private initTime: number | undefined;
	private timer$$: Subscription | undefined;

	constructor(
		private changeDetector: ChangeDetectorRef,
		private zone: NgZone,
		private platformService: PlatformService
	) {}

	ngOnChanges(changes: { elapsedTimeInMillis: SimpleChange }) {
		if (!changes.elapsedTimeInMillis) {
			return;
		}
		if (this.platformService.isBrowser) {
			this.initTime = Date.now();
			changes.elapsedTimeInMillis.firstChange && this.computeElapsedTime();
			this.updateTimer();
		}
	}

	ngOnDestroy() {
		this.unsubscribe();
	}

	private unsubscribe() {
		if (this.timer$$) {
			this.timer$$.unsubscribe();
		}
	}

	private updateTimer() {
		this.unsubscribe();
		const timer$ = timer(0, REFRESH_TIMER_MILLISECONDS).pipe(
			tap(() => {
				this.computeElapsedTime();
				this.changeDetector.detectChanges();
			})
		);

		this.zone.runOutsideAngular(() => (this.timer$$ = timer$.subscribe()));
	}

	private computeElapsedTime() {
		const timeFromInit = differenceInMilliseconds(Date.now(), this.initTime!);
		const elapsedTime = this.elapsedTimeInMillis ? timeFromInit + this.elapsedTimeInMillis : timeFromInit;

		this.seconds = this.formatTimeUnit(fromMillisecondsToSeconds(elapsedTime) % 60);
		this.minutes = this.formatTimeUnit(fromMillisecondsToMinutes(elapsedTime) % 60);
		this.hours = this.formatTimeUnit(fromMillisecondsToHours(elapsedTime));
	}

	private formatTimeUnit(unit: number): string {
		return padStart(floor(unit).toString(), 2, "0");
	}
}
